import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import PersonVehicleRegistrationDialog from './PersonVehicleRegistrationDialog';
import {
  getPeopleVehicleRegistrations,
  deleteVehicleRegistration,
} from '../../services/vehicleService';
import { VEHICLE_REG_GRID_COLUMNS } from '../../constants/personVehicleRegistrationFormConstants';
import FormSpinner from 'components/FormSpinner';

const PersonVehicleRegistrationsTab = ({ personId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingRegistration, setEditingRegistration] = useState(null);
  const canEditPerson = usePermissions(PERMISSION_RESOURCES.PERSON).Edit;
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadRegistrations = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPeopleVehicleRegistrations(personId),
      onSuccess: (data) => setRegistrations(data),
    });
  }, [dispatch, personId]);

  const handleAdd = useCallback(() => {
    setEditingRegistration(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingRegistration(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingRegistration(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadRegistrations();
  }, [handleFormClose, loadRegistrations]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${
          row.PlateNumber || 'vehicle registration'
        }" record?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () => deleteVehicleRegistration(row.ptsRegistrationID),
            onSuccess: () => {
              dispatch(notify('Vehicle registration deleted successfully', 'success'));
              loadRegistrations();
            },
          });
        },
        'Delete Vehicle Registration'
      );
    },
    [dispatch, requestConfirm, loadRegistrations]
  );

  useEffect(() => {
    loadRegistrations();
  }, [loadRegistrations]);

  return (
    <Box className="person-names-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Vehicle Registration"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPerson}
        />
      </Box>

      <CommonDataGrid
        rows={registrations}
        columns={VEHICLE_REG_GRID_COLUMNS}
        getRowId={(row) => row.ptsRegistrationID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Vehicle Registrations' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditPerson },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPerson },
        }}
      />

      {formOpen && (
        <PersonVehicleRegistrationDialog
          open={formOpen}
          personId={personId}
          registration={editingRegistration}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default PersonVehicleRegistrationsTab;
